import { useState } from "react"
import { Head, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Mail, RefreshCw, CheckCircle, AlertCircle } from "lucide-react"
import logo from "@/assets/logo-25segundos-horizontal.png"
import { PageLayout } from "@/components/VocationalTest/PageLayout"

interface Props {
  email: string
  lastSentAt: string | null
  flash?: {
    notice?: string
    alert?: string
  }
}

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return "Nenhum e-mail enviado ainda"

  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)

  if (diffMins < 1) return "Agora mesmo"
  if (diffMins < 60) return `Há ${diffMins} minuto${diffMins > 1 ? "s" : ""}`
  if (diffHours < 24) return `Há ${diffHours} hora${diffHours > 1 ? "s" : ""}`
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  })
}

export default function AwaitingConfirmation({ email, lastSentAt, flash }: Props) {
  const [isResending, setIsResending] = useState(false)

  const handleResend = () => {
    setIsResending(true)
    router.post("/reenviar-confirmacao", {}, {
      onFinish: () => setIsResending(false),
      preserveState: true
    })
  }

  const handleLogout = () => {
    router.delete("/logout")
  }

  return (
    <>
      <Head title="Confirme seu E-mail - Orientação Profissional" />
      <PageLayout>
        <div className="w-full max-w-md">
          <Card className="w-full p-8 shadow-elegant">
            <div className="space-y-6">
              <div className="flex justify-center">
                <img src={logo} alt="25Segundos" className="h-16" />
              </div>

              <div className="flex justify-center">
                <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center">
                  <Mail className="w-10 h-10 text-amber-600" />
                </div>
              </div>

              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-title-blue">Verifique seu E-mail</h2>
                <p className="text-muted-foreground">
                  Enviamos um link de confirmação para:
                </p>
                <p className="font-semibold text-foreground bg-muted px-4 py-2 rounded-lg inline-block">
                  {email}
                </p>
              </div>

              {flash?.notice && (
                <div className="p-4 bg-green-100 text-green-800 rounded-lg text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  {flash.notice}
                </div>
              )}

              {flash?.alert && (
                <div className="p-4 bg-red-100 text-red-800 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {flash.alert}
                </div>
              )}

              <div className="text-center text-sm text-muted-foreground space-y-1">
                <p>Clique no link do e-mail para ativar sua conta.</p>
                <p>Verifique também a pasta de spam.</p>
              </div>

              <div className="border-t pt-4 space-y-4">
                <div className="text-center text-sm text-muted-foreground">
                  Último e-mail enviado: {formatTimeAgo(lastSentAt)}
                </div>

                <Button
                  onClick={handleResend}
                  variant="outline"
                  className="w-full"
                  disabled={isResending}
                >
                  {isResending ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Reenviar e-mail de confirmação
                    </>
                  )}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
                  >
                    Digitou o e-mail errado? Sair e criar nova conta
                  </button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </PageLayout>
    </>
  )
}
